import axios from 'axios';
import {
  ACTIVATE_USER,
  ASSIGN_USER_TO_PROJECT,
  CREATE_USER,
  DEACTIVATE_USER,
  GET_COMPANIES,
  GET_USERS,
  UPDATE_USER,
  get_request_config
} from '@/utils/system_constants/constant.js';
import { fetch_organization_projects, has_current_organization } from '@/services/organization_service.js';
import {
  get_empty_assign_form,
  is_coordinator_role,
  parse_api_data,
  unwrap_items
} from './user_management_helpers';

export const user_management_action_methods = {
  sanitize_user_name_field(form_name, field_name) {
    const form = this.get_user_contact_form(form_name);
    if (!form) return;
    const field_value = this.get_user_name_field_value(form, field_name);
    const sanitized_value = String(field_value || '').replace(/[^A-Za-z\s'-]/g, '').slice(0, 50);
    this.set_user_name_field_value(form, field_name, sanitized_value);
  },

  sanitize_contact_field(form_name, field_name) {
    const form = this.get_user_contact_form(form_name);
    if (!form) return;
    const field_value = this.get_contact_number_field_value(form, field_name);
    const sanitized_value = String(field_value || '').replace(/\D/g, '').slice(0, 10);
    this.set_contact_number_field_value(form, field_name, sanitized_value);
    if (field_name === 'phone_number') this.sync_same_as_phone_fields(form_name);
  },

  sync_same_as_phone_fields(form_name) {
    const form = this.get_user_contact_form(form_name);
    if (!form) return;
    if (form.sms_same_as_phone) form.sms_number = form.phone_number;
    if (form.whatsapp_same_as_phone) form.whatsapp_number = form.phone_number;
  },

  toggle_same_as_phone(form_name, field_name) {
    const form = this.get_user_contact_form(form_name);
    if (!form) return;

    if (field_name === 'sms_number') {
      if (form.sms_same_as_phone) form.sms_number = form.phone_number;
    } else if (field_name === 'whatsapp_number' && form.whatsapp_same_as_phone) {
      form.whatsapp_number = form.phone_number;
    }

    this.sync_toggled_same_as_phone_field(form, field_name);
  },

  sync_toggled_same_as_phone_field(form, field_name) {
    if (field_name === 'sms_number') {
      this.sync_toggled_sms_number(form);
      return;
    }

    if (field_name === 'whatsapp_number') this.sync_toggled_whatsapp_number(form);
  },

  sync_toggled_sms_number(form) {
    if (!form.sms_same_as_phone) return;
    form.sms_number = form.phone_number;
  },

  sync_toggled_whatsapp_number(form) {
    if (!form.whatsapp_same_as_phone) return;
    form.whatsapp_number = form.phone_number;
  },

  async fetch_projects() {
    this.projects_loading = true;

    try {
      const payload = has_current_organization() ? await fetch_organization_projects() : await axios.get(GET_COMPANIES, get_request_config());
      this.projects = unwrap_items(payload);
    } catch {
      this.projects = [];
    } finally {
      this.projects_loading = false;
    }
  },

  async fetch_assigned_project_ids(user_id) {
    if (!user_id) return [];
    if (!this.projects.length) await this.fetch_projects();
    this.assigned_projects_loading = true;

    try {
      const responses = await Promise.allSettled(
        this.projects.map((project) =>
          axios.get(GET_USERS, get_request_config({ params: { project_id: project.id, page: 1, page_size: 100 } }))
            .then((response) => ({ project, response }))
        )
      );

      return responses
        .filter((result) => result.status === 'fulfilled')
        .filter(({ value }) => {
          const response_data = parse_api_data(value.response.data);
          const users = Array.isArray(response_data.data) ? response_data.data : [];
          return users.some((user) => String(user.user_id || user.id) === String(user_id));
        })
        .map(({ value }) => String(value.project.id));
    } catch {
      return [];
    } finally {
      this.assigned_projects_loading = false;
    }
  },

  async open_assign_user_modal(user) {
    if (user.is_active === false) {
      this.show_warning('User Deactivated', `You cannot assign ${user.name} because this user is deactivated. Please activate the user first.`);
      return;
    }

    this.assign_form = { ...get_empty_assign_form(), user };
    this.show_project_dropdown = false;
    if (!this.projects.length) await this.fetch_projects();
    const assigned_project_ids = await this.fetch_assigned_project_ids(user.id);
    this.assign_form.project_ids = assigned_project_ids;
    this.assign_form.original_project_ids = assigned_project_ids;
  },

  async create_user() {
    const validation_message = this.validate_create_user();
    if (validation_message) return this.show_error(validation_message);

    this.creating_user = true;
    const loader = await this.show_loader('Creating user...');

    try {
      const payload = {
        first_name: this.create_form.first_name.trim(),
        last_name: this.create_form.last_name.trim(),
        email: this.create_form.email.trim(),
        phone_number: this.create_form.phone_number.trim(),
        sms_number: this.create_form.sms_number.trim(),
        whatsapp_number: this.create_form.whatsapp_number.trim(),
        role: this.create_form.role
      };

      const selected_entity_type = this.get_selected_create_entity_type();
      if (is_coordinator_role(this.create_form.role) && selected_entity_type) {
        payload.entity_type_id = selected_entity_type.id;
        if (selected_entity_type.project_id) payload.project_id = selected_entity_type.project_id;
      }

      const response = await axios.post(CREATE_USER, payload, get_request_config());
      const response_data = parse_api_data(response.data);
      if (response_data?.status && response_data.status !== 'success') throw new Error(response_data.message || 'User creation failed.');
      this.close_modal('create_user_modal');
      this.reset_create_form();
      await this.fetch_users();
      this.show_success(response_data.message || 'User created successfully.');
    } catch (error) {
      const response_data = parse_api_data(error?.response?.data);
      this.show_error(response_data.message || response_data.data || error.message || 'Failed to create user.');
    } finally {
      this.creating_user = false;
      this.close_loader(loader);
    }
  },

  async update_user() {
    const validation_message = this.validate_edit_user();
    if (validation_message) return this.show_error(validation_message);

    this.updating_user = true;
    const loader = await this.show_loader('Updating user...');

    try {
      const payload = {
        user_id: this.edit_form.user_id,
        first_name: this.edit_form.first_name.trim(),
        last_name: this.edit_form.last_name.trim(),
        email: this.edit_form.email.trim(),
        phone_number: this.edit_form.phone_number.trim(),
        sms_number: this.edit_form.sms_number.trim(),
        whatsapp_number: this.edit_form.whatsapp_number.trim(),
        role_id: Number(this.edit_form.role_id)
      };

      const selected_role = this.roles.find((role) => String(role.id) === String(this.edit_form.role_id));
      const selected_entity_type = this.get_selected_edit_entity_type();
      if (is_coordinator_role(selected_role?.role) && selected_entity_type) {
        payload.entity_type_id = selected_entity_type.id;
        if (selected_entity_type.project_id) payload.project_id = selected_entity_type.project_id;
      }

      const response = await axios.post(UPDATE_USER, payload, get_request_config());
      const response_data = parse_api_data(response.data);
      if (response_data?.status && response_data.status !== 'success') throw new Error(response_data.message || 'User update failed.');
      this.close_modal('edit_user_modal');
      await this.fetch_users();
      this.show_success(response_data.message || 'User updated successfully.');
    } catch (error) {
      this.show_error(this.parse_error_message(error, 'Failed to update user.'));
    } finally {
      this.updating_user = false;
      this.close_loader(loader);
    }
  },

  toggle_user_status(user) {
    const should_activate = user.is_active === false;
    const action_label = should_activate ? 'activate' : 'deactivate';

    this.show_confirm(`${should_activate ? 'Activate' : 'Deactivate'} User`, `Are you sure you want to ${action_label} ${user.name}?`, async () => {
      const loader = await this.show_loader(`${should_activate ? 'Activating' : 'Deactivating'} user...`);

      try {
        const response = await axios.post(should_activate ? ACTIVATE_USER : DEACTIVATE_USER, { user_id: user.id }, get_request_config());
        const response_data = parse_api_data(response.data);
        if (response_data?.status && response_data.status !== 'success') throw new Error(response_data.message || 'User status update failed.');
        await this.fetch_users();
        this.show_success(response_data.message || 'User status updated.');
      } catch (error) {
        this.show_error(this.parse_error_message(error, 'Failed to update user status.'));
      } finally {
        this.close_loader(loader);
      }
    });
  },

  async assign_user_to_project() {
    if (!this.assign_form.user?.id) return this.show_error('Please select a user.');
    const selected_project_ids = Array.isArray(this.assign_form.project_ids) ? this.assign_form.project_ids : [];
    const original_project_ids = Array.isArray(this.assign_form.original_project_ids) ? this.assign_form.original_project_ids : [];
    if (!selected_project_ids.length && !original_project_ids.length) return this.show_error('Please select at least one project.');

    const original_ids = new Set(original_project_ids.map(String));
    const selected_ids = new Set(selected_project_ids.map(String));
    const changed_project_ids = [
      ...selected_project_ids.filter((project_id) => !original_ids.has(String(project_id))),
      ...original_project_ids.filter((project_id) => !selected_ids.has(String(project_id)))
    ];

    if (!changed_project_ids.length) {
      this.close_modal('assign_user_modal');
      this.assign_form = get_empty_assign_form();
      this.show_project_dropdown = false;
      return this.show_success('User project assignments are already up to date.');
    }

    const assigner_id = this.$store.getters.currentUserId;
    if (!assigner_id) return this.show_error('Current user could not be resolved.');

    this.assigning_user = true;
    const loader = await this.show_loader('Assigning user...');

    try {
      const responses = await Promise.all(
        changed_project_ids.map((project_id) =>
          axios.post(ASSIGN_USER_TO_PROJECT, { project: Number(project_id), assigner: Number(assigner_id), assignee: Number(this.assign_form.user.id) }, get_request_config())
        )
      );

      responses.forEach((response) => {
        const response_data = parse_api_data(response.data);
        if (response_data?.status && response_data.status !== 'success') throw new Error(response_data.message || 'Project assignment failed.');
      });

      this.close_modal('assign_user_modal');
      this.assign_form = get_empty_assign_form();
      this.show_project_dropdown = false;
      this.show_success(`User assignment updated for ${changed_project_ids.length} project${changed_project_ids.length === 1 ? '' : 's'}.`);
    } catch (error) {
      this.show_error(this.parse_error_message(error, 'Failed to assign user.'));
    } finally {
      this.assigning_user = false;
      this.close_loader(loader);
    }
  }
};
